const multipart = { headers: { "Content-Type": "multipart/form-data" } };

// serviceKey ("MY_ORG" / "MY_BUSINESS") is sent as a query param (not a header) on the
// id-less self-service create calls — custom headers trigger a CORS preflight that isn't
// allow-listed, but query params don't, and it's only the create endpoints that need it.
export const createComplianceApi = (api, serviceKey) => ({
  // PAN Business
  getPANBusiness: (fpoId) => api.get(`/fpo/${fpoId}/pan/business`),
  getAllPANBusiness: () => api.get("/fpo/pan/business"),
  addPANBusiness: (fpoId, formData) => api.post(`/fpo/${fpoId}/pan/business`, formData, multipart),
  getPANBusinessById: (fpoId, id) => api.get(`/fpo/${fpoId}/pan/business/${id}`),
  updatePANBusiness: (fpoId, id, data) => api.patch(`/fpo/${fpoId}/pan/business/${id}`, data, multipart),
  deletePANBusiness: (fpoId, id) => api.delete(`/fpo/${fpoId}/pan/business/${id}`),
  updatePanBusinessStatus: (fpoId, id, statusData) => api.patch(`/fpo/${fpoId}/pan/business/${id}/status`, statusData),
  downloadPANRegistration: (fpoId) => api.get(`/fpo/${fpoId}/pan/download`, { responseType: "blob" }),

  // PAN Individual
  getPANIndividual: () => api.get("/fpo/pan/individual/my"),
  getAllPANIndividual: () => api.get(`/fpo/pan/individual?serviceKey=${serviceKey}`),
  getMyPANIndividual: () => api.get("/fpo/pan/individual/my"),
  addPANIndividual: (_fpoId, formData) => api.post(`/fpo/pan/individual?serviceKey=${serviceKey}`, formData, multipart),
  getPANIndividualById: (_fpoId, id) => api.get(`/fpo/pan/individual/${id}`),
  updatePANIndividual: (_fpoId, id, data) => api.patch(`/fpo/pan/individual/${id}`, data, multipart),
  deletePANIndividual: (_fpoId, id) => api.delete(`/fpo/pan/individual/${id}`),
  updatePanIndividualStatus: (_fpoId, id, statusData) => api.patch(`/fpo/pan/individual/${id}/status`, statusData),

  // FPO/FPC Registration
  getFPCById: (_fpoId, id) => api.get(`/fpo/fpc/${id}`),
  fpcRegistration: (_fpoId, formData) => api.post(`/fpo/fpc?serviceKey=${serviceKey}`, formData, multipart),
  getAllFpoFpcRegistrations: () => api.get(`/fpo/fpc?serviceKey=${serviceKey}`),
  getFPC: () => api.get("/fpo/fpc/my"),
  updateFpoFpcStatus: (_fpoId, fpcId, statusData) => api.patch(`/fpo/fpc/${fpcId}/status`, statusData),
  updateFPCRegistration: (_fpoId, id, data) => api.patch(`/fpo/fpc/${id}`, data, multipart),
  deleteFPCCompany: (_fpoId, id) => api.delete(`/fpo/fpc/${id}`),

  // ISO
  registerIso: (_fpoId, formData) => api.post(`/fpo/iso?serviceKey=${serviceKey}`, formData, multipart),
  getIsoRegistration: () => api.get("/fpo/iso/my"),
  getAllIsoRegistration: () => api.get(`/fpo/iso?serviceKey=${serviceKey}`),
  getMyIsoRegistration: () => api.get("/fpo/iso/my"),
  getIsoRegistrationById: (_fpoId, isoId) => api.get(`/fpo/iso/${isoId}`),
  updateIsoRegistration: (_fpoId, isoId, formData) => api.patch(`/fpo/iso/${isoId}`, formData, multipart),
  updateIsoStatus: (_fpoId, id, statusData) => api.patch(`/fpo/iso/${id}/status`, statusData),
  deleteIsoRegistration: (_fpoId, isoId) => api.delete(`/fpo/iso/${isoId}`),

  // IEC
  registerIec: (_fpoId, formData) => api.post(`/fpo/iec?serviceKey=${serviceKey}`, formData, multipart),
  getIecRegistrations: () => api.get("/fpo/iec/my"),
  getAllIecRegistrations: () => api.get(`/fpo/iec?serviceKey=${serviceKey}`),
  getMyIecRegistrations: () => api.get("/fpo/iec/my"),
  getIecRegistrationById: (_fpoId, iecId) => api.get(`/fpo/iec/${iecId}`),
  updateIecRegistration: (_fpoId, iecId, formData) => api.patch(`/fpo/iec/${iecId}`, formData, multipart),
  updateIecStatus: (_fpoId, id, statusData) => api.patch(`/fpo/iec/${id}/status`, statusData),
  deleteIecRegistration: (_fpoId, iecId) => api.delete(`/fpo/iec/${iecId}`),

  // MSME
  getMSMEByFpoId: () => api.get("/fpo/msme/my"),
  getAllMSME: () => api.get(`/fpo/msme?serviceKey=${serviceKey}`),
  getMyMSME: () => api.get("/fpo/msme/my"),
  msmeRegistration: (_fpoId, formData) => api.post(`/fpo/msme?serviceKey=${serviceKey}`, formData, multipart),
  getMSMEById: (_fpoId, id) => api.get(`/fpo/msme/${id}`),
  updateMSMERegistration: (_fpoId, id, data) => api.patch(`/fpo/msme/${id}`, data, multipart),
  updateMSMEStatus: (_fpoId, id, statusData) => api.patch(`/fpo/msme/${id}/status`, statusData),
  deleteMSME: (_fpoId, id) => api.delete(`/fpo/msme/${id}`),

  // GST
  registerGst: (_fpoId, formData) => api.post(`/fpo/gst?serviceKey=${serviceKey}`, formData, multipart),
  getGstRegistrations: () => api.get("/fpo/gst/my"),
  getAllGstRegistrations: () => api.get(`/fpo/gst?serviceKey=${serviceKey}`),
  getMyGstRegistrations: () => api.get("/fpo/gst/my"),
  getGstRegistrationById: (_fpoId, gstId) => api.get(`/fpo/gst/${gstId}`),
  updateGstRegistration: (_fpoId, gstId, formData) => api.patch(`/fpo/gst/${gstId}`, formData, multipart),
  updateGstStatus: (_fpoId, id, statusData) => api.patch(`/fpo/gst/${id}/status`, statusData),
  deleteGstRegistration: (_fpoId, gstId) => api.delete(`/fpo/gst/${gstId}`),

  // Company Registration (statutory compliance sub-type — distinct from the MyBusiness "Company"
  // tenant module; this is a compliance document form, reused as-is for both services)
  registerCompany: (_fpoId, formData) => api.post(`/fpo/company?serviceKey=${serviceKey}`, formData, multipart),
  getCompanyRegistrations: () => api.get("/fpo/company/my"),
  getAllCompanyRegistrations: () => api.get(`/fpo/company?serviceKey=${serviceKey}`),
  getCompanyRegistrationById: (_fpoId, companyId) => api.get(`/fpo/company/${companyId}`),
  updateCompanyRegistration: (_fpoId, companyId, formData) => api.patch(`/fpo/company/${companyId}`, formData, multipart),
  updateCompanyStatus: (_fpoId, companyId, statusData) => api.patch(`/fpo/company/${companyId}/status`, statusData),
  deleteCompanyRegistration: (_fpoId, companyId) => api.delete(`/fpo/company/${companyId}`),

  // Trade License
  registerTradeLicense: (_fpoId, formData) => api.post(`/fpo/tradeLicense?serviceKey=${serviceKey}`, formData, multipart),
  getTradeLicenses: () => api.get("/fpo/tradeLicense/my"),
  getAllTradeLicenses: () => api.get(`/fpo/tradeLicense?serviceKey=${serviceKey}`),
  getMyTradeLicenses: () => api.get("/fpo/tradeLicense/my"),
  getTradeLicenseById: (_fpoId, licenseId) => api.get(`/fpo/tradeLicense/${licenseId}`),
  updateTradeLicense: (_fpoId, licenseId, formData) => api.patch(`/fpo/tradeLicense/${licenseId}`, formData, multipart),
  updateTradeLicensesStatus: (_fpoId, id, statusData) => api.patch(`/fpo/tradeLicense/${id}/status`, statusData),
  deleteTradeLicense: (_fpoId, licenseId) => api.delete(`/fpo/tradeLicense/${licenseId}`),

  // Labour License
  registerLabourLicense: (_fpoId, formData) => api.post(`/fpo/labour?serviceKey=${serviceKey}`, formData, multipart),
  getLabourLicenses: () => api.get("/fpo/labour/my"),
  getAllLabourLicenses: () => api.get(`/fpo/labour?serviceKey=${serviceKey}`),
  getMyLabourLicenses: () => api.get("/fpo/labour/my"),
  getLabourLicenseById: (_fpoId, licenseId) => api.get(`/fpo/labour/${licenseId}`),
  updateLabourLicense: (_fpoId, licenseId, formData) => api.patch(`/fpo/labour/${licenseId}`, formData, multipart),
  updateLabourLicensesStatus: (_fpoId, id, statusData) => api.patch(`/fpo/labour/${id}/status`, statusData),
  deleteLabourLicense: (_fpoId, licenseId) => api.delete(`/fpo/labour/${licenseId}`),
  downloadLabourLicense: (fpoId) => api.get(`/fpo/${fpoId}/labour/download`, { responseType: "blob" }),

  // Shops & Establishment
  registerShopsEstablishment: (_fpoId, formData) => api.post(`/fpo/shop?serviceKey=${serviceKey}`, formData, multipart),
  getShopsEstablishment: () => api.get("/fpo/shop/my"),
  getAllShopsEstablishment: () => api.get(`/fpo/shop?serviceKey=${serviceKey}`),
  getMyShopsEstablishment: () => api.get("/fpo/shop/my"),
  getShopsEstablishmentById: (_fpoId, establishmentId) => api.get(`/fpo/shop/${establishmentId}`),
  updateShopsEstablishment: (_fpoId, establishmentId, formData) => api.patch(`/fpo/shop/${establishmentId}`, formData, multipart),
  updateShopsEstablishmentStatus: (_fpoId, id, statusData) => api.patch(`/fpo/shop/${id}/status`, statusData),
  deleteShopsEstablishment: (_fpoId, establishmentId) => api.delete(`/fpo/shop/${establishmentId}`),

  // FSSAI
  registerFSSAI: (_fpoId, formData) => api.post(`/fpo/fssai?serviceKey=${serviceKey}`, formData, multipart),
  getFSSAIRegistrations: () => api.get("/fpo/fssai/my"),
  getAllFSSAIRegistrations: () => api.get(`/fpo/fssai?serviceKey=${serviceKey}`),
  getMyFSSAIRegistrations: () => api.get("/fpo/fssai/my"),
  getFSSAIRegistrationById: (_fpoId, fssaiId) => api.get(`/fpo/fssai/${fssaiId}`),
  updateFSSAIRegistration: (_fpoId, fssaiId, formData) => api.patch(`/fpo/fssai/${fssaiId}`, formData, multipart),
  updateFSSAIStatus: (_fpoId, id, statusData) => api.patch(`/fpo/fssai/${id}/status`, statusData),
  deleteFSSAIRegistration: (_fpoId, fssaiId) => api.delete(`/fpo/fssai/${fssaiId}`),
});
